package com.milta.feature.team.presentation

import com.milta.feature.team.domain.entity.Driver

/**
 * State for the web dashboard's team management screen (MIL-49 — `WebTeamViewModel`). Consolidates
 * what mobile splits across two screens/ViewModels ([EnterpriseTeamViewModel] for the list + add
 * flow, [DriverDetailViewModel] for the remove flow) into a single state, since the web dashboard
 * shows the confirmation dialog inline on top of the team list rather than navigating to a
 * dedicated detail screen.
 */
data class WebTeamState(
    val isLoading: Boolean = false,
    val drivers: List<Driver> = emptyList(),
    // Resolved once at load time via GetMyTeamUseCase().firstOrNull() (no JWT claim exposes it
    // client-side today, see WebTeamViewModel.loadTeam doc — same limitation as EnterpriseTeamViewModel).
    val enterpriseId: String? = null,
    /** Set when [WebTeamViewModel.loadTeam] fails (enterpriseId resolution or drivers fetch) —
     * "server_error" | "offline". Must be checked before treating `drivers.isEmpty()` as the
     * genuine "no drivers yet" state (mirrors mobile's [TeamState.error]/[TeamScreen]). */
    val error: String? = null,
    val codeInput: String = "",
    val isSubmittingCode: Boolean = false,
    /** "invalid_code" | "not_found" | "already_member" | "server_error" | "offline". */
    val addDriverError: String? = null,
    /** Non-null while the remove confirmation dialog is open — holds the id of the driver about
     * to be removed. Null means no dialog is showing. */
    val pendingRemovalDriverId: String? = null,
    val isRemoving: Boolean = false,
    /** "not_found" | "on_active_delivery" | "last_owner" | "server_error" | "offline". */
    val removalError: String? = null,
)
